import React from "react";

import { toast } from "react-toastify";

import { useAuth0 } from "../../../auth/auth0";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { type DocumentType, getDocumentObjectUrl, getDocumentUrl } from "../../../apis/documentApi";

const OBJECT_URL_LIFETIME_MS = 60000;

type Props = {
	documentType: DocumentType;
	children: React.ReactNode;
	disabled?: boolean;
	openInNewTab?: boolean;
	style?: React.CSSProperties;
};

const DocumentLink = ({ documentType, children, disabled = false, openInNewTab = false, style }: Props) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	const openDocument = async () => {
		if (disabled) return;

		const newTab = openInNewTab ? window.open("", "_blank") : null;

		try {
			if (!userAuth.currentClientId) {
				throw new Error("No client is currently selected");
			}

			const accessToken = await getTokenSilently();

			if (!openInNewTab) {
				window.location.href = await getDocumentUrl(documentType, userAuth.currentClientId, accessToken);
				return;
			}

			toast.info("Opening document, please wait...");

			const objectUrl = await getDocumentObjectUrl(documentType, userAuth.currentClientId, accessToken);

			if (newTab) {
				newTab.opener = null;
				newTab.location.href = objectUrl;
			} else {
				window.location.href = objectUrl;
			}

			setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_LIFETIME_MS);
		} catch {
			newTab?.close();
			toast.error(
				openInNewTab
					? "Sorry, that document could not be opened. Please try again."
					: "Sorry, that document could not be downloaded. Please try again.",
			);
		}
	};

	return (
		<a
			onClick={() => void openDocument()}
			style={{ cursor: disabled ? "default" : "pointer", ...style }}
			role="button"
			tabIndex={0}
		>
			{children}
		</a>
	);
};

export default DocumentLink;
